import React, { useEffect, useState, useMemo } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import {
 Row,
 Col,
 Card,
 Button,
 Table,
 Form,
 Badge,
 Modal,
} from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";

import AppPagination from "../Common/AppPagination";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";
import { useSelector } from "react-redux";
import PermissionWrapper from "../PermissionWrapper";

const ITEMS_PER_PAGE = 10;

const BoqAmendmentList = () => {
 const navigate = useNavigate();
 const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const projectFromUrl = query.get("project") || "";
  const workOrderFromUrl = query.get("workOrder") || "";
 const user = useSelector((state) => state.auth?.userDetails || {});

 const [amendments, setAmendments] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
  const [selectedProject, setSelectedProject] = usePersistentState("boq_amend_project", projectFromUrl);
  const [selectedWO, setSelectedWO] = usePersistentState("boq_amend_wo", workOrderFromUrl);
 const [search, setSearch] = usePersistentState("boq_amend_search", "");
 const [fromDate, setFromDate] = usePersistentState("boq_amend_from", "");
 const [toDate, setToDate] = usePersistentState("boq_amend_to", "");
 const [currentPage, setCurrentPage] = usePersistentState("boq_amend_page", 1);
 const [selectedAmendment, setSelectedAmendment] = useState(null);

 useEffect(() => {
 fetchWorkOrders();
 fetchAmendments();
 }, []);

  const filteredWorkOrders = useMemo(() => {
    const activeProject = selectedProject || projectFromUrl;
    if (!activeProject) return workOrders;
    return workOrders.filter((w) => {
      const pId = w.project?._id || w.project;
      return String(pId) === String(activeProject);
    });
  }, [workOrders, selectedProject, projectFromUrl]);

 // ================= FETCH WORK ORDERS =================
 const fetchWorkOrders = async () => {
 try {
 const res = await api.get("/work-orders?limit=1000");
 if (res.data.success) setWorkOrders(res.data.data);
 } catch {
 swal("Error", "Failed to load work orders", "error");
 }
 };

 // ================= FETCH AMENDMENTS =================
 const fetchAmendments = async (woId = "", from = "", to = "") => {
 try {
 const res = await api.get("/boq-amendments", {
 params: { 
 workOrder: woId || selectedWO,
 fromDate: from || fromDate,
 toDate: to || toDate,
 },
 });
 if (res.data.success) setAmendments(res.data.data);
 } catch {
 swal("Error", "Failed to load amendments", "error");
 }
 };

 // ================= FILTER BY WORK ORDER =================
  const handleFilter = (e) => {
    const value = e.target.value;
    setSelectedWO(value);
    if (value) {
      const foundWo = workOrders.find((w) => String(w._id) === String(value));
      const autoProj = foundWo?.project?._id || foundWo?.project;
      if (autoProj) setSelectedProject(String(autoProj));
    }
    setCurrentPage(1);
    fetchAmendments(value, fromDate, toDate);
  };

 const handleDateChange = (type, value) => {
 if (type === "from") {
 setFromDate(value);
 fetchAmendments(selectedWO, value, toDate);
 } else {
 setToDate(value);
 fetchAmendments(selectedWO, fromDate, value);
 }
 setCurrentPage(1);
 };

 // ================= APPROVE =================
 const approveAmendment = async (id) => {
 const confirm = await swal({
 title: "Approve Amendment?",
 text: "This will permanently update BOQ quantities.",
 icon: "warning",
 buttons: true,
 });

 if (!confirm) return;

 try {
 await api.put(`/boq-amendments/${id}/approve`);
 swal("Success", "Amendment Approved", "success");
 fetchAmendments(selectedWO);
 } catch {
 swal("Error", "Approval failed", "error");
 }
 };
useEffect(() => {
 const params = new URLSearchParams(location.search);
 const woFromQuery = params.get("workOrder");

 fetchWorkOrders();

 if (woFromQuery) {
 setSelectedWO(woFromQuery);
 fetchAmendments(woFromQuery);
 } else {
 fetchAmendments();
 }
}, [location.search]);

 // ================= REJECT =================
 const rejectAmendment = async (id) => {
 const confirm = await swal({
 title: "Reject Amendment?",
 icon: "warning",
 buttons: true,
 });

 if (!confirm) return;

 try {
 await api.put(`/boq-amendments/${id}/reject`);
 swal("Success", "Amendment Rejected", "success");
 fetchAmendments(selectedWO);
 } catch {
 swal("Error", "Rejection failed", "error");
 }
 };

 // ================= SEARCH =================
 const filteredAmendments = useMemo(() => {
 return amendments.filter((a) =>
 a.workOrder?.woNo?.toLowerCase().includes(search.toLowerCase())
 );
 }, [amendments, search]);

 // ================= PAGINATION =================
 const totalPages = Math.ceil(
 filteredAmendments.length / ITEMS_PER_PAGE
 );

 const paginatedData = filteredAmendments.slice(
 (currentPage - 1) * ITEMS_PER_PAGE,
 currentPage * ITEMS_PER_PAGE
 );

 return (
 <>
 <PageTitle
 activeMenu="BOQ Amendments"
 motherMenu="Work Order"
 />

 <Row>
 <Col lg={12}>
 <Card className="shadow-sm">

 {/* HEADER */}
 <Card.Header className="d-flex flex-nowrap justify-content-between align-items-center gap-2">

 <div className="d-flex flex-nowrap gap-2 flex-grow-1" style={{ minWidth: 0 }}>
 <Form.Select
 size="sm"
 style={{ flex: "1 1 260px", minWidth: 0 }}
 value={selectedWO}
 onChange={handleFilter}
 >
 <option value="">All Work Orders</option>
 {filteredWorkOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo}{wo.label ? ` – ${wo.label}` : ""}
 </option>
 ))}
 </Form.Select>

 <Form.Control
 size="sm"
 type="text"
 placeholder="Search by Work Order..."
 value={search}
 onChange={(e) => {
 setSearch(e.target.value);
 setCurrentPage(1);
 }}
 style={{ flex: "1 1 220px", minWidth: 0 }}
 />

 <Form.Control
 size="sm"
 type="date"
 value={fromDate}
 onChange={(e) => handleDateChange("from", e.target.value)}
 style={{ flex: "0 1 180px", minWidth: 0 }}
 />

 <Form.Control
 size="sm"
 type="date"
 value={toDate}
 onChange={(e) => handleDateChange("to", e.target.value)}
 style={{ flex: "0 1 180px", minWidth: 0 }}
 />
 </div>

 <PermissionWrapper user={user} required={["create:projects"]}>
 <Button
 variant="primary"
 className="text-nowrap"
 onClick={() =>
 navigate(
 selectedWO
 ? `/boq-amendments/add?workOrder=${selectedWO}`
 : "/boq-amendments/add"
 )
 }
 >
 + Create Amendment
 </Button>
 </PermissionWrapper>
 </Card.Header>

 {/* BODY */}
 <Card.Body>

 <Table bordered hover responsive className="align-middle">
 <thead
 className="text-center"
 style={{
 backgroundColor: "#343a40",
 color: "#030202",
 }}
 >
 <tr>
 <th>#</th>
 <th>Work Order</th>
 <th>Amendment No</th>
 <th>Before Qty</th>
 <th>Change</th>
 <th>After Qty</th>
 <th>Total Impact</th>
 <th>Status</th>
 <th>Date</th>
 <th>Action</th>
 </tr>
 </thead>

 <tbody>
 {paginatedData.length > 0 ? (
 paginatedData.map((a, i) => {

 const beforeQty = a.items?.reduce(
 (sum, item) =>
 sum + (item.oldQuantity || 0),
 0
 );

 const changeQty = a.items?.reduce(
 (sum, item) =>
 sum + (item.difference || 0),
 0
 );

 const afterQty = a.items?.reduce(
 (sum, item) =>
 sum + (item.newQuantity || 0),
 0
 );

 return (
 <tr key={a._id}>
 <td>
 {(currentPage - 1) *
 ITEMS_PER_PAGE +
 i +
 1}
 </td>

 <td>
 {a.workOrder?.woNo || a.workOrder?.woNumber || "—"}
 </td>

 <td className="text-center">
 #{a.amendmentNo}
 {a.items?.some((item) => item.isNew) && (
 <Badge bg="success" className="ms-2">
 New
 </Badge>
 )}
 </td>

 <td className="text-center">
 <Badge bg="secondary">
 {beforeQty}
 </Badge>
 </td>

 <td className="text-center">
 <Badge
 bg={
 changeQty > 0
 ? "success"
 : changeQty < 0
 ? "danger"
 : "secondary"
 }
 >
 {changeQty > 0
 ? `+${changeQty}`
 : changeQty}
 </Badge>
 </td>

 <td className="text-center">
 <Badge bg="primary">
 {afterQty}
 </Badge>
 </td>

 <td className="fw-bold text-end">
 ₹{" "}
 {a.totalImpact?.toLocaleString()}
 </td>

 <td className="text-center">
 <Badge
 bg={
 a.status === "Approved"
 ? "success"
 : a.status === "Rejected"
 ? "danger"
 : "warning"
 }
 >
 {a.status}
 </Badge>
 </td>

 <td>
 {new Date(
 a.createdAt
 ).toLocaleDateString()}
 </td>

 <td>
 <div className="d-flex gap-1">
 <Button
 size="sm"
 variant="outline-info"
 onClick={() => setSelectedAmendment(a)}
 >
 View
 </Button>

 {a.status === "Pending" ? (
 <>
 <PermissionWrapper user={user} required={["edit:boqs", "edit:projects"]}>
 <Button
 size="sm"
 variant="primary"
 onClick={() =>
 navigate(
 selectedWO
 ? `/boq-amendments/edit/${a._id}?workOrder=${selectedWO}`
 : `/boq-amendments/edit/${a._id}`
 )
 }
 >
 Edit
 </Button>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["approve:projects"]}>
 <Button
 size="sm"
 variant="success"
 onClick={() =>
 approveAmendment(
 a._id
 )
 }
 >
 Approve
 </Button>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["reject:projects"]}>
 <Button
 size="sm"
 variant="danger"
 onClick={() =>
 rejectAmendment(
 a._id
 )
 }
 >
 Reject
 </Button>
 </PermissionWrapper>
 </>
 ) : null}
 </div>
 </td>
 </tr>
 );
 })
 ) : (
 <tr>
 <td
 colSpan="10"
 className="text-center py-4 text-muted"
 >
 No amendments found.
 </td>
 </tr>
 )}
 </tbody>
 </Table>

 <AppPagination
 currentPage={currentPage}
 totalPages={totalPages}
 totalCount={filteredAmendments.length}
 itemsLength={paginatedData.length}
 limit={ITEMS_PER_PAGE}
 onPageChange={setCurrentPage}
 />
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <Modal
 show={Boolean(selectedAmendment)}
 onHide={() => setSelectedAmendment(null)}
 size="xl"
 centered
 >
 <Modal.Header closeButton>
 <Modal.Title>
 BOQ Amendment #{selectedAmendment?.amendmentNo}
 </Modal.Title>
 </Modal.Header>
 <Modal.Body>
 {selectedAmendment && (
 <>
 <Row className="mb-3">
 <Col md={4}>
 <div className="text-muted small">Work Order</div>
 <div className="fw-bold">
 {selectedAmendment.workOrder?.woNo || "-"}
 </div>
 </Col>
 <Col md={4}>
 <div className="text-muted small">Status</div>
 <Badge
 bg={
 selectedAmendment.status === "Approved"
 ? "success"
 : selectedAmendment.status === "Rejected"
 ? "danger"
 : "warning"
 }
 >
 {selectedAmendment.status}
 </Badge>
 </Col>
 <Col md={4}>
 <div className="text-muted small">Date</div>
 <div>
 {new Date(selectedAmendment.createdAt).toLocaleDateString()}
 </div>
 </Col>
 </Row>

 <div className="table-responsive">
 <Table bordered hover size="sm" className="align-middle">
 <thead className="table-dark text-center">
 <tr>
 <th>#</th>
 <th>WLI No</th>
 <th>HSN/SAC</th>
 <th>Item Name</th>
 <th>Description</th>
 <th>Unit</th>
 <th>Old Qty</th>
 <th>New Qty</th>
 <th>Difference</th>
 <th>Old Price</th>
 <th>New Price</th>
 <th>Impact</th>
 </tr>
 </thead>
 <tbody>
 {selectedAmendment.items?.map((item, index) => (
 <tr key={index}>
 <td>{index + 1}</td>
 <td>{item.wlino || "-"}</td>
 <td>{item.hsnSac || "-"}</td>
 <td>
 {item.itemName || "-"}
 {item.isNew && (
 <Badge bg="success" className="ms-2">
 New
 </Badge>
 )}
 </td>
 <td>{item.description || "-"}</td>
 <td>{item.unit || "-"}</td>
 <td className="text-end">{item.oldQuantity || 0}</td>
 <td className="text-end">{item.newQuantity || 0}</td>
 <td className="text-center">
 <Badge
 bg={
 item.difference > 0
 ? "success"
 : item.difference < 0
 ? "danger"
 : "secondary"
 }
 >
 {item.difference > 0
 ? `+${item.difference}`
 : item.difference || 0}
 </Badge>
 </td>
 <td className="text-end text-muted">
 ₹ {Number(item.oldPrice !== undefined ? item.oldPrice : item.price || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
 </td>
 <td className="text-end fw-semibold">
 ₹ {Number(item.price || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
 </td>
 <td className="text-end fw-bold">
 ₹ {(item.impactAmount !== undefined
 ? Number(item.impactAmount)
 : (Number(item.difference || 0) * Number(item.price || 0))
 ).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
 </td>
 </tr>
 ))}
 </tbody>
 <tfoot>
 <tr>
 <td colSpan="11" className="text-end fw-bold">
 Total Impact
 </td>
 <td className="text-end fw-bold">
 Rs.{" "}
 {Number(
 selectedAmendment.totalImpact || 0
 ).toLocaleString()}
 </td>
 </tr>
 </tfoot>
 </Table>
 </div>

 {selectedAmendment.remarks && (
 <div className="mt-3">
 <div className="text-muted small">Remarks</div>
 <div>{selectedAmendment.remarks}</div>
 </div>
 )}
 </>
 )}
 </Modal.Body>
 </Modal>
 </>
 );
};

export default BoqAmendmentList;
